import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { toast } from '@buffer-mono/popcorn'

import { useAccount, useIsOwner } from '~publish/contexts/AccountContext'

import { NewSettingsPageLayout } from '../../../shared/components/NewSettingsPageLayout'
import { SettingsPageSkeleton } from '../../../shared/components/SettingsPageSkeleton'
import {
  useAPIClients,
  useRegisterAPIClient,
  useUpdateAPIClient,
} from '../../hooks'
import type { RegisterAPIClientInput } from '../../hooks'
import { ApiClientForm } from '../APIClientAction/ApiClientForm'

import type { JSX } from 'react'

export function APIClientPage(): JSX.Element {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const { loading: isAccountLoading } = useAccount()
  const isOwner = useIsOwner()
  const { clients, isLoading } = useAPIClients()
  const { registerClient, isLoading: isRegistering } = useRegisterAPIClient()
  const { updateClient, isLoading: isUpdating } = useUpdateAPIClient()

  if (isAccountLoading || isLoading) {
    return (
      <NewSettingsPageLayout title="" pageTitle="">
        <SettingsPageSkeleton />
      </NewSettingsPageLayout>
    )
  }

  if (!isOwner) {
    return <Navigate to="/settings/api?tab=app-clients" replace />
  }

  const isNewClient = clientId === 'new'

  const client = isNewClient
    ? null
    : (clients?.find((c) => c.clientId === clientId) ?? null)

  if (!isNewClient && !client) {
    return <Navigate to="/settings/api?tab=app-clients" replace />
  }

  const handleSubmit = async (
    input: RegisterAPIClientInput,
  ): Promise<{ success: true } | { success: false; error: string }> => {
    if (client) {
      return updateClient({
        clientId: client.clientId,
        clientName: input.clientName,
        redirectUris: input.redirectUris,
        ...(input.privacyPolicyUri && {
          privacyPolicyUri: input.privacyPolicyUri,
        }),
        ...(input.logoUri !== undefined && { logoUri: input.logoUri }),
        ...(input.scopes && { scopes: input.scopes }),
      })
    }

    return registerClient(input)
  }

  const handleBack = (): void => {
    navigate('/settings/api?tab=app-clients')
  }

  const handleSuccess = (): void => {
    const message = client
      ? 'Client updated successfully.'
      : 'Client created successfully.'

    toast.success(message)
    navigate('/settings/api?tab=app-clients')
  }

  return (
    <ApiClientForm
      client={client}
      onSubmit={handleSubmit}
      isSubmitting={isUpdating || isRegistering}
      onBack={handleBack}
      onSuccess={handleSuccess}
    />
  )
}
